import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { CircleInstReminderIcon } from '@bufferapp/components';
import ClockIcon from '@bufferapp/ui/Icon/Icons/Clock';
import MessageIcon from '@bufferapp/ui/Icon/Icons/Message';
import PersonIcon from '@bufferapp/ui/Icon/Icons/Person';
import ThreadIcon from '@bufferapp/ui/Icon/Icons/Thread';
import { grayDark } from '@bufferapp/ui/style/colors';
import styled from 'styled-components';
import { Button, Link } from '@bufferapp/ui';
import { actions as dataFetchActions } from '@bufferapp/async-data-fetch';
import { PostActionLabel } from '~/post/types';
import { PostEntity } from '~/post/PostEntity';
import { movePostToDrafts } from '../../post/thunks/movePostToDrafts';
import CardFooter from '../CardFooter';
import {
  ButtonWrapper,
  CardFooterContent,
  CommentIconWrapper,
  EditButton,
  IconWrapper,
  Message,
  PreMessage,
  ThreadCounter,
  ThreadIconWrapper,
  UserTagsIconWrapper,
} from '../CardFooter/styles';
import ConfirmDeleteButton from '../ConfirmDeleteButton';
import { SentPostFooterActions } from './SentPostFooterActions';

export const PhoneIconWrapper = styled.div`
  background-color: ${grayDark};
  border-radius: 100%;
  height: 16px;
  width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    height: 10px;
    width: 10px;
  }
`;

const renderIcon = (
  hasError,
  isSent,
  isCustomScheduled,
  isInstagramReminder,
  isPastReminder
) => {
  if (
    (!hasError && !isCustomScheduled && !isInstagramReminder) ||
    isPastReminder
  )
    return;

  return (
    <>
      {isInstagramReminder && !isSent && !hasError ? (
        <CircleInstReminderIcon color="instagram" />
      ) : null}
      {isCustomScheduled && !hasError && !isInstagramReminder ? (
        <ClockIcon color={grayDark} />
      ) : null}
    </>
  );
};

const PostFooter = ({
  post,
  postId,
  profileId,
  isDeleting,
  isConfirmingDelete,
  isWorking,
  onDeleteConfirmClick,
  onRequeueClick,
  onEditClick,
  onShareNowClick,
  onShareAgainClick,
  onShareLinkClick,
  postAction,
  dragging,
  serviceLink,
  isSent,
  isManager,
  isPastReminder,
  day,
  dueTime,
  commentEnabled,
  commentText,
  hasCommentEnabled,
  hasUserTags,
  shouldShowEditButton,
  threadCounter,
}) => {
  const dispatch = useDispatch();
  const hasError = PostEntity.hasError(post);
  const comment = { commentEnabled, commentText };
  const hideButtons = !isManager || isSent || isPastReminder;
  const showSentPostActions = isSent || isPastReminder;
  const deletingMessage = (isDeleting || isConfirmingDelete) && 'Deleting...';
  const submittingMessage = isWorking && (post?.workingMessage || 'Sharing...');
  const actionMessage = deletingMessage || submittingMessage || '';
  const isPerformingAction = !!actionMessage;

  // eslint-disable-next-line no-nested-ternary
  const message = isPastReminder ? day : hasError ? '' : postAction;
  const isCustomScheduled = PostEntity.isCustomScheduled(post);
  const isInstagramReminder = PostEntity.isInstagramReminder(post);
  const icon = renderIcon(
    hasError,
    isSent,
    isCustomScheduled,
    isInstagramReminder,
    isPastReminder
  );
  const messageColor = isSent ? grayDark : '';
  const preMessage = isPastReminder ? dueTime : '';
  const hasFirstComment = hasCommentEnabled && comment.commentEnabled;

  const refetchQueue = () =>
    dispatch(
      dataFetchActions.fetch({
        name: 'queuedPosts',
        args: {
          profileId,
          isFetchingMore: false,
          hideLoading: true,
          count: 300,
        },
      })
    );

  const hideAllButtons = hideButtons || isPerformingAction || serviceLink;
  const [firstAction, ...stackedActions] = [
    {
      id: 'share-now',
      title: hasError ? 'Retry now' : 'Share Now',
      callback: onShareNowClick,
    },
    {
      id: 'requeue',
      title: 'Re-add to Queue',
      callback: hasError && onRequeueClick,
    },
    {
      id: 'move-to-drafts',
      title: PostActionLabel.MOVE_POST_TO_DRAFTS,
      callback: () =>
        dispatch(movePostToDrafts({ postId, profileId })).then(refetchQueue),
    },
  ].filter((action) => !!action.callback);

  const executeStackedAction = ({ id }) => {
    const selectedAction = stackedActions.find((action) => action.id === id);

    if (selectedAction) selectedAction.callback();
  };

  return (
    <CardFooter isDragging={dragging}>
      <CardFooterContent>
        {threadCounter > 0 && (
          <>
            <ThreadIconWrapper>
              <ThreadIcon
                color={grayDark}
                size="medium"
                role="img"
                title="This is a Thread post"
              />
            </ThreadIconWrapper>
            <ThreadCounter
              type="p"
              textColor={messageColor}
              data-testid="threads-counter"
            >
              {threadCounter}
            </ThreadCounter>
          </>
        )}
        {icon && <IconWrapper>{icon}</IconWrapper>}
        {serviceLink ? (
          <Link href={serviceLink} unstyled newTab>
            <Message type="p" textColor={messageColor}>
              {message}
            </Message>
          </Link>
        ) : (
          <>
            {preMessage && (
              <PreMessage type="p">{`${preMessage} &nbsp;`}</PreMessage>
            )}
            <Message type="p" textColor={messageColor}>
              {message}
            </Message>
          </>
        )}
        {isSent && post.via && (
          <Message type="p" textColor={messageColor}>
            &nbsp;{`Via ${post.via}`}
          </Message>
        )}
        {hasFirstComment && (
          <CommentIconWrapper title="Post includes a comment">
            <MessageIcon />
          </CommentIconWrapper>
        )}
        {hasUserTags && (
          <UserTagsIconWrapper
            title="Post includes tagged users"
            hasFirstComment={hasFirstComment}
          >
            <PersonIcon />
          </UserTagsIconWrapper>
        )}
      </CardFooterContent>
      {showSentPostActions && (
        <SentPostFooterActions
          postId={postId}
          onShareAgainClick={onShareAgainClick}
          onShareLinkClick={onShareLinkClick}
          serviceLink={serviceLink}
        />
      )}
      {!hideAllButtons && (
        <ButtonWrapper data-testid="post-footer-buttons">
          {onDeleteConfirmClick && (
            <ConfirmDeleteButton onDeleteClick={onDeleteConfirmClick} />
          )}
          {shouldShowEditButton && onEditClick && (
            <EditButton
              label="Edit"
              size="small"
              onClick={onEditClick}
              type="text"
            />
          )}
          {firstAction && (
            <Button
              size="small"
              type="secondary"
              isSplit={!!stackedActions}
              items={stackedActions}
              label={firstAction.title}
              onClick={firstAction.callback}
              onSelectClick={executeStackedAction}
            />
          )}
        </ButtonWrapper>
      )}
      {isPerformingAction && <Message type="p">{actionMessage}</Message>}
    </CardFooter>
  );
};

PostFooter.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    via: PropTypes.string,
    workingMessage: PropTypes.string,
  }).isRequired,
  postId: PropTypes.string.isRequired,
  profileId: PropTypes.string.isRequired,
  isDeleting: PropTypes.bool,
  isConfirmingDelete: PropTypes.bool,
  isWorking: PropTypes.bool,
  onDeleteConfirmClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onShareNowClick: PropTypes.func,
  onShareAgainClick: PropTypes.func,
  onShareLinkClick: PropTypes.func,
  postAction: PropTypes.string,
  dragging: PropTypes.bool,
  onRequeueClick: PropTypes.func,
  serviceLink: PropTypes.string,
  isSent: PropTypes.bool,
  isManager: PropTypes.bool,
  isPastReminder: PropTypes.bool,
  day: PropTypes.string,
  dueTime: PropTypes.string,
  hasCommentEnabled: PropTypes.bool,
  commentEnabled: PropTypes.bool,
  commentText: PropTypes.string,
  hasUserTags: PropTypes.bool,
  shouldShowEditButton: PropTypes.bool,
  threadCounter: PropTypes.number,
};

PostFooter.defaultProps = {
  isDeleting: false,
  isConfirmingDelete: false,
  isWorking: false,
  onDeleteConfirmClick: null,
  onEditClick: null,
  onShareNowClick: null,
  onShareAgainClick: null,
  onShareLinkClick: null,
  onRequeueClick: null,
  serviceLink: null,
  day: null,
  dueTime: null,
  dragging: false,
  isManager: true,
  isSent: false,
  isPastReminder: false,
  hasCommentEnabled: false,
  shouldShowEditButton: true,
  commentEnabled: false,
  commentText: null,
  hasUserTags: false,
  postAction: null,
  threadCounter: 0,
};

export default PostFooter;
